import { useState, useMemo, useEffect } from 'react'
import { Plus, Mails, Info, BarChart3, Calendar as CalendarIcon, Send, TrendingUp, CheckCircle2, AlertCircle, Copy } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useAuth } from '@/auth/context/AuthContext'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CampaniasTable } from '@/modules/multichannel/components/CampaniasTable'
import { CampaniasFilters } from '@/modules/multichannel/components/CampaniasFilters'
import { ProgresoLaboratorios } from '@/modules/multichannel/components/ProgresoLaboratorios'
import { PendientesEnvio } from '@/modules/multichannel/components/PendientesEnvio'
import { HtmlPreviewModal } from '@/modules/multichannel/components/HtmlPreviewModal'
import { WhatsAppPreviewModal } from '@/modules/multichannel/components/WhatsAppPreviewModal'
import { ReporteModal } from '@/modules/cronograma/components/ReporteModal'
import { ReporteWhatsAppModal } from '@/modules/cronograma/components/ReporteWhatsAppModal'
import { useCampanias, useFiltersData, useCategorizacion } from '@/modules/multichannel/hooks/useCampanias'
import { useTeam } from '@/shared/context/TeamContext'

import type { CampaniaFilters, Campania } from '@/modules/multichannel/types/campania'

// Tipos de campaña que incluyen WhatsApp
const WHATSAPP_TIPOS = ['Whatsapp', 'Email + Whatsapp']
const EMAIL_TIPOS = ['Email', 'Email + Whatsapp']

// Determinar qué tipo de reporte mostrar según el tipo de campaña
function getReportTypes(tipo: string): { showEmail: boolean; showWhatsApp: boolean } {
  return {
    showEmail: EMAIL_TIPOS.includes(tipo),
    showWhatsApp: WHATSAPP_TIPOS.includes(tipo),
  }
}

// Función para formatear fecha sin problemas de timezone
function formatDateLocal(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Función para obtener el primer y último día del mes actual
function getCurrentMonthRange() {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  return {
    fecha_desde: formatDateLocal(firstDay),
    fecha_hasta: formatDateLocal(lastDay),
  }
}

const STAT_CARDS = [
  { key: 'total', label: 'Total Campañas', icon: Mails, hex: '#57a6d8' },
  { key: 'activas', label: 'Programadas', icon: TrendingUp, hex: '#8d82bc' },
  { key: 'enviadas', label: 'Enviadas', icon: CheckCircle2, hex: '#2fbc80' },
  { key: 'pendientes', label: 'Pendientes', icon: AlertCircle, hex: '#f6c23e' },
] as const

export function CampaniasSeguimiento() {
  useDocumentTitle('Seguimiento')
  const navigate = useNavigate()
  const { user } = useAuth()

  // Check if user can create campaigns (mailing_admin or admin)
  const isTonalAdmin = user?.groups?.includes('admin') || user?.is_superuser
  const canCreateCampania = user?.groups?.includes('mailing_admin') || isTonalAdmin

  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [reporteEmailModalOpen, setReporteEmailModalOpen] = useState(false)
  const [reporteWhatsAppModalOpen, setReporteWhatsAppModalOpen] = useState(false)
  const [selectedCampania, setSelectedCampania] = useState<Campania | null>(null)
  const [showInfo, setShowInfo] = useState(false)
  const [previewCampaniaId, setPreviewCampaniaId] = useState<number | null>(null)
  const [previewNombre, setPreviewNombre] = useState('')
  const [previewWACampaniaId, setPreviewWACampaniaId] = useState<number | null>(null)
  const [previewWANombre, setPreviewWANombre] = useState('')
  const [duplicarDialogOpen, setDuplicarDialogOpen] = useState(false)
  const [duplicarId, setDuplicarId] = useState<number | null>(null)
  const currentMonthRange = useMemo(() => getCurrentMonthRange(), [])

  const [filters, setFilters] = useState<CampaniaFilters>({
    ...currentMonthRange, // Filtrar por mes actual por defecto
    orden: 'fecha_publicacion', // Orden ascendente por fecha
  })

  const { data: filtersData } = useFiltersData(filters)

  // Auto-filtrar por laboratorio del área seleccionada
  const areaLabMarcaId = useMemo(() => {
    if (!isClientArea || !selectedArea?.laboratorio_nombre || !filtersData) return undefined
    const marca = filtersData.marcas_principales.find(
      (m) => m.nombre === selectedArea.laboratorio_nombre
    )
    return marca?.id
  }, [isClientArea, selectedArea?.laboratorio_nombre, filtersData])

  // Combinar filtros del usuario con el filtro de área
  const effectiveFilters = useMemo<CampaniaFilters>(() => {
    if (areaLabMarcaId) {
      return { ...filters, marca_principal_id: areaLabMarcaId }
    }
    return filters
  }, [filters, areaLabMarcaId])

  // Reset página al cambiar de área
  useEffect(() => { setPage(1) }, [selectedTeam])

  const { data: campaniasData, isLoading } = useCampanias(effectiveFilters, page)

  const campanias = campaniasData?.items || []
  const totalCount = campaniasData?.count || 0
  const totalPages = Math.ceil(totalCount / pageSize)

  // Categorización: IDs de campañas con reporte generado
  const campaniaIdsConReporte = useMemo(() =>
    campanias
      .filter(c => c.estado_reporte?.toLowerCase().includes('generado'))
      .map(c => c.id),
    [campanias]
  )
  const { data: categorizaciones } = useCategorizacion(campaniaIdsConReporte)

  // Stats calculados
  const stats = useMemo(() => ({
    total: totalCount,
    activas: campanias.filter((c) => c.estado_envio === 'Programado').length,
    enviadas: campanias.filter((c) => c.estado_envio === 'Enviado').length,
    pendientes: campanias.filter((c) => c.estado_envio === 'Pendiente').length,
  }), [campanias, totalCount])

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1)
  }

  const handleFilterChange = (newFilters: CampaniaFilters) => {
    setFilters(newFilters)
    setPage(1) // Reset a página 1 cuando cambian filtros
  }

  const handleClearFilters = () => {
    setFilters({
      ...currentMonthRange, // Mantener el filtro del mes actual al limpiar
      orden: 'fecha_publicacion',
    })
    setPage(1)
  }

  const handleViewDetails = (id: number) => {
    window.location.href = `/multichannel/campania/${id}`
  }

  const handleViewReport = (campaniaId: number) => {
    // Abrir reporte en nueva pestaña según el tipo de campaña
    const campania = campanias.find(c => c.id === campaniaId)
    const tipo = campania?.tipo || ''
    const { showEmail, showWhatsApp } = getReportTypes(tipo)

    if (showWhatsApp && !showEmail) {
      window.open(`/multichannel/reporte-whatsapp/${campaniaId}`, '_blank')
    } else {
      window.open(`/multichannel/reporte/${campaniaId}`, '_blank')
    }
  }

  const handleCreateReport = (campaniaId: number) => {
    // Abrir modal para crear nuevo reporte según el tipo de campaña
    const campania = campanias.find(c => c.id === campaniaId)
    if (campania) {
      setSelectedCampania(campania)
      const { showEmail, showWhatsApp } = getReportTypes(campania.tipo || '')

      // Para campañas combinadas (Email + WhatsApp), mostrar primero el de Email
      // El usuario puede crear ambos reportes secuencialmente
      if (showEmail) {
        setReporteEmailModalOpen(true)
      } else if (showWhatsApp) {
        setReporteWhatsAppModalOpen(true)
      }
    }
  }

  const handleDuplicate = (id: number) => {
    setDuplicarId(id)
    setDuplicarDialogOpen(true)
  }

  const handleDuplicarTipo = (tipo: 'normal' | 'reenvio') => {
    setDuplicarDialogOpen(false)
    if (duplicarId) {
      navigate(`/multichannel/campania/${duplicarId}/duplicar?tipo=${tipo}`)
    }
  }

  const handleCreateWhatsAppReport = (campaniaId: number) => {
    // Abrir modal específico para WhatsApp (para campañas combinadas)
    const campania = campanias.find(c => c.id === campaniaId)
    if (campania) {
      setSelectedCampania(campania)
      setReporteWhatsAppModalOpen(true)
    }
  }

  const handleEditReport = (campaniaId: number) => {
    const campania = campanias.find(c => c.id === campaniaId)
    if (campania) {
      setSelectedCampania(campania)
      setReporteEmailModalOpen(true)
    }
  }

  const handleEditWhatsAppReport = (campaniaId: number) => {
    const campania = campanias.find(c => c.id === campaniaId)
    if (campania) {
      setSelectedCampania(campania)
      setReporteWhatsAppModalOpen(true)
    }
  }

  const handlePreviewHtml = (campaniaId: number, nombre: string) => {
    setPreviewCampaniaId(campaniaId)
    setPreviewNombre(nombre)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Mails className="h-6 w-6 text-black dark:text-white" />
            Seguimiento de Campañas
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Gestiona y monitorea tus campañas de email marketing
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
            onClick={() => navigate('/multichannel/analytics')}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </Button>
          <Button
            variant="outline"
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
            onClick={() => navigate('/multichannel/calendario')}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            Calendario
          </Button>
          {isTonalAdmin && (
            <Button
              variant="outline"
              className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
              onClick={() => window.open('https://app2.fromdoppler.com/Campaigns/BasicInfo', '_blank')}
            >
              <Send className="mr-2 h-4 w-4" />
              Doppler
            </Button>
          )}
          {canCreateCampania && (
            <Button className="bg-teal-800 hover:bg-teal-900 text-white" onClick={() => navigate('/multichannel/campania/nueva')}>
              <Plus className="mr-2 h-4 w-4" />
              Nueva Campaña
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards - estilo reporte */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {STAT_CARDS.map(({ key, label, icon: Icon, hex }) => (
          <div
            key={key}
            className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            style={{ background: `linear-gradient(to right, ${hex}20, transparent 60%)` }}
          >
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
                <div className="rounded-full p-1.5" style={{ backgroundColor: `${hex}15` }}>
                  <Icon className="h-4 w-4" style={{ color: hex }} />
                </div>
              </div>
              <span className="text-2xl font-bold text-foreground leading-tight">
                {stats[key]}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Progreso y Pendientes */}
      <div className="grid gap-4 md:grid-cols-2">
        <ProgresoLaboratorios filters={effectiveFilters} />
        <PendientesEnvio filters={effectiveFilters} />
      </div>

      {/* Filtros */}
      <CampaniasFilters
        filters={filters}
        filtersData={filtersData}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        areaLabMarcaId={areaLabMarcaId}
      />

      {/* Tabla */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Campañas
            {campaniasData && (
              <Badge className="badge-gradient hover:opacity-90">
                {campaniasData.count}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CampaniasTable
            campanias={campanias}
            isLoading={isLoading}
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            onViewDetails={handleViewDetails}
            onViewReport={handleViewReport}
            onCreateReport={handleCreateReport}
            onCreateWhatsAppReport={handleCreateWhatsAppReport}
            onEditReport={handleEditReport}
            onEditWhatsAppReport={handleEditWhatsAppReport}
            onDuplicate={handleDuplicate}
            onPreviewHtml={handlePreviewHtml}
            onPreviewWhatsApp={(id, nombre) => {
              setPreviewWACampaniaId(id)
              setPreviewWANombre(nombre)
            }}
            canManage={canCreateCampania}
            categorizaciones={categorizaciones}
          />
        </CardContent>
      </Card>

      {/* Modal de Reporte Email */}
      {selectedCampania && (
        <ReporteModal
          open={reporteEmailModalOpen}
          onClose={() => {
            setReporteEmailModalOpen(false)
            // Si es campaña combinada, preguntar si quiere crear reporte WhatsApp
            const { showWhatsApp } = getReportTypes(selectedCampania.tipo || '')
            if (showWhatsApp && !reporteWhatsAppModalOpen) {
              // Opcional: podría abrir automáticamente el modal de WhatsApp
              // setReporteWhatsAppModalOpen(true)
            }
            if (!reporteWhatsAppModalOpen) {
              setSelectedCampania(null)
            }
          }}
          campaniaId={selectedCampania.id}
          nombreCampania={selectedCampania.nombre_campania}
          tieneReporte={selectedCampania.estado_reporte === 'Generado'}
        />
      )}

      {/* Modal de Reporte WhatsApp */}
      {selectedCampania && (
        <ReporteWhatsAppModal
          open={reporteWhatsAppModalOpen}
          onClose={() => {
            setReporteWhatsAppModalOpen(false)
            if (!reporteEmailModalOpen) {
              setSelectedCampania(null)
            }
          }}
          campaniaId={selectedCampania.id}
          nombreCampania={selectedCampania.nombre_campania}
          nombreLaboratorio={selectedCampania.marca}
          fechaPublicacion={selectedCampania.fecha_publicacion}
          tieneReporte={selectedCampania.estado_reporte === 'Generado'}
        />
      )}

      {/* Modal de Preview HTML */}
      <HtmlPreviewModal
        campaniaId={previewCampaniaId}
        nombre={previewNombre}
        open={previewCampaniaId !== null}
        onClose={() => {
          setPreviewCampaniaId(null)
          setPreviewNombre('')
        }}
      />

      {/* Modal de Preview WhatsApp */}
      <WhatsAppPreviewModal
        campaniaId={previewWACampaniaId}
        nombre={previewWANombre}
        open={previewWACampaniaId !== null}
        onClose={() => {
          setPreviewWACampaniaId(null)
          setPreviewWANombre('')
        }}
      />

      {/* Dialog selección tipo de duplicación */}
      <Dialog open={duplicarDialogOpen} onOpenChange={setDuplicarDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Duplicar Campaña
            </DialogTitle>
            <DialogDescription>
              Selecciona cómo quieres duplicar esta campaña
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => handleDuplicarTipo('normal')}
              className="flex flex-col gap-2 rounded-lg border-2 border-transparent p-4 text-left hover:border-teal-500 hover:bg-teal-500/5 transition-colors"
            >
              <div className="flex items-center gap-2 font-medium">
                <Copy className="h-4 w-4 text-teal-500" />
                Normal
              </div>
              <p className="text-xs text-muted-foreground">
                Conserva configuración. Asunto, HTML y fechas en blanco.
              </p>
            </button>
            <button
              onClick={() => handleDuplicarTipo('reenvio')}
              className="flex flex-col gap-2 rounded-lg border-2 border-transparent p-4 text-left hover:border-purple-500 hover:bg-purple-500/5 transition-colors"
            >
              <div className="flex items-center gap-2 font-medium">
                <Send className="h-4 w-4 text-purple-500" />
                Reenvío
              </div>
              <p className="text-xs text-muted-foreground">
                Conserva HTML y asunto. Fechas y estado de envío en blanco.
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
